import { useState, useEffect } from 'react'
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  createColumnHelper,
  type PaginationState,
} from '@tanstack/react-table'
import { Link } from 'react-router-dom'
import { Eye, Search } from 'lucide-react'
import type { BenefitVoucher, VoucherFilters, VoucherStatus } from '../types'
import { BenefitStatusBadge } from './BenefitStatusBadge'

const VOUCHER_STATUS_LABELS: Record<VoucherStatus, string> = {
  pending: 'Pendiente',
  redeemed: 'Canjeado',
  expired: 'Expirado',
  cancelled: 'Cancelado',
}

const VOUCHER_TYPE_LABELS: Record<string, string> = {
  physical_gift_voucher: 'Vale de regalo físico',
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '-'
  return new Date(dateStr).toLocaleDateString('es-CL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}

function formatDateTime(dateStr: string | null): string {
  if (!dateStr) return '-'
  return new Date(dateStr).toLocaleDateString('es-CL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function formatCLP(amount: number | null | undefined): string {
  if (amount === null || amount === undefined) return '-'
  return `$${amount.toLocaleString('es-CL')}`
}

const columnHelper = createColumnHelper<BenefitVoucher>()

const columns = [
  columnHelper.accessor('voucher_code', {
    header: 'Código',
    cell: ({ getValue }) => (
      <span className="font-mono text-sm font-medium">{getValue()}</span>
    ),
  }),
  columnHelper.accessor('client', {
    header: 'Cliente',
    cell: ({ getValue }) => {
      const client = getValue()
      return (
        <div>
          <Link
            to={`/clients/${client.id}`}
            className="text-primary-600 hover:text-primary-800 font-medium"
          >
            {client.name} {client.last_name}
          </Link>
          <p className="text-xs text-gray-500">{client.rut}</p>
        </div>
      )
    },
  }),
  columnHelper.accessor('voucher_type', {
    header: 'Tipo',
    cell: ({ getValue }) => (
      <span className="px-2 py-1 bg-indigo-100 text-indigo-800 rounded text-xs font-medium">
        {VOUCHER_TYPE_LABELS[getValue()] ?? getValue()}
      </span>
    ),
  }),
  columnHelper.accessor('status', {
    header: 'Estado',
    size: 100,
    cell: ({ getValue }) => <BenefitStatusBadge status={getValue()} />,
  }),
  columnHelper.accessor('gift_description', {
    header: 'Descripción',
    cell: ({ getValue }) => (
      <span className="text-sm text-gray-600 truncate max-w-[200px] block">
        {getValue() ?? '-'}
      </span>
    ),
  }),
  columnHelper.accessor('valid_from', {
    header: 'Válido desde',
    size: 110,
    cell: ({ getValue }) => (
      <span className="text-sm">{formatDate(getValue())}</span>
    ),
  }),
  columnHelper.accessor('valid_until', {
    header: 'Válido hasta',
    size: 110,
    cell: ({ getValue }) => (
      <span className="text-sm">{formatDate(getValue())}</span>
    ),
  }),
  columnHelper.accessor('redeemed_at', {
    header: 'Canjeado',
    size: 140,
    cell: ({ getValue, row }) => {
      const redeemedAt = getValue()
      if (!redeemedAt) return <span className="text-gray-400 text-sm">-</span>
      return (
        <div className="text-sm">
          <p>{formatDateTime(redeemedAt)}</p>
          {row.original.redeemed_by && (
            <p className="text-xs text-gray-500">
              por {row.original.redeemed_by.name}
            </p>
          )}
        </div>
      )
    },
  }),
  columnHelper.accessor('redeemed_product', {
    header: 'Producto',
    cell: ({ getValue }) => {
      const product = getValue()
      if (!product) return <span className="text-gray-400 text-sm">-</span>
      return <span className="text-sm">{product}</span>
    },
  }),
  columnHelper.accessor('redeemed_amount', {
    header: 'Monto',
    size: 100,
    cell: ({ getValue }) => {
      const amount = getValue()
      if (amount === null || amount === undefined) return <span className="text-gray-400 text-sm">-</span>
      return <span className="text-sm font-medium">{formatCLP(amount)}</span>
    },
  }),
  columnHelper.display({
    id: 'actions',
    header: '',
    size: 60,
    cell: ({ row }) => {
      if (row.original.status !== 'pending') return null
      return (
        <Link
          to={`/vouchers/redeem?code=${row.original.voucher_code}`}
          className="text-primary-600 hover:text-primary-800 transition-colors"
          title="Canjear"
        >
          <Eye size={18} />
        </Link>
      )
    },
  }),
]

interface LocalFilters {
  status: string
  voucher_type: string
  search: string
}

function filtersToLocal(filters: VoucherFilters): LocalFilters {
  return {
    status: filters.status ?? '',
    voucher_type: filters.voucher_type ?? '',
    search: filters.search ?? '',
  }
}

interface VouchersTableProps {
  data: BenefitVoucher[]
  total: number
  page: number
  limit: number
  onPageChange: (page: number) => void
  onLimitChange: (limit: number) => void
  isLoading: boolean
  filters: VoucherFilters
  onApplyFilters: (filters: Partial<VoucherFilters>) => void
  onClearFilters: () => void
}

export function VouchersTable({
  data,
  total,
  page,
  limit,
  onPageChange,
  onLimitChange,
  isLoading,
  filters,
  onApplyFilters,
  onClearFilters,
}: VouchersTableProps) {
  const [localFilters, setLocalFilters] = useState<LocalFilters>(() => filtersToLocal(filters))

  // Sync local state when applied filters change externally (e.g. clearFilters)
  useEffect(() => {
    setLocalFilters(filtersToLocal(filters))
  }, [filters])

  const hasFilters =
    filters.status !== undefined ||
    filters.voucher_type !== undefined ||
    filters.search !== undefined

  const handleApply = () => {
    onApplyFilters({
      status: (localFilters.status || undefined) as VoucherStatus | undefined,
      voucher_type: localFilters.voucher_type || undefined,
      search: localFilters.search || undefined,
    })
  }

  const handleClear = () => {
    onClearFilters()
  }

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleApply()
    }
  }

  const pagination: PaginationState = {
    pageIndex: page - 1,
    pageSize: limit,
  }

  const pageCount = Math.ceil(total / limit)

  const table = useReactTable({
    data,
    columns,
    pageCount,
    state: { pagination },
    onPaginationChange: (updater) => {
      const newPagination =
        typeof updater === 'function' ? updater(pagination) : updater
      if (newPagination.pageSize !== limit) {
        onLimitChange(newPagination.pageSize)
      }
      if (newPagination.pageIndex + 1 !== page) {
        onPageChange(newPagination.pageIndex + 1)
      }
    },
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
  })

  if (isLoading) {
    return <div className="animate-pulse h-96 bg-gray-100 rounded-lg" />
  }

  return (
    <div>
      {/* Filter bar */}
      <div className="p-4 border-b flex flex-wrap items-center gap-3">
        {/* Search input */}
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={localFilters.search}
            onChange={(e) => setLocalFilters((prev) => ({ ...prev, search: e.target.value }))}
            onKeyDown={handleSearchKeyDown}
            placeholder="Buscar código, cliente o RUT..."
            className="input pl-9 w-64 text-sm"
          />
        </div>

        <select
          value={localFilters.status}
          onChange={(e) => setLocalFilters((prev) => ({ ...prev, status: e.target.value }))}
          className="input w-auto text-sm"
        >
          <option value="">Estado: Todos</option>
          {Object.entries(VOUCHER_STATUS_LABELS).map(([key, label]) => (
            <option key={key} value={key}>
              {label}
            </option>
          ))}
        </select>

        <select
          value={localFilters.voucher_type}
          onChange={(e) => setLocalFilters((prev) => ({ ...prev, voucher_type: e.target.value }))}
          className="input w-auto text-sm"
        >
          <option value="">Tipo: Todos</option>
          {Object.entries(VOUCHER_TYPE_LABELS).map(([key, label]) => (
            <option key={key} value={key}>
              {label}
            </option>
          ))}
        </select>

        <button onClick={handleApply} className="btn btn-primary text-sm">
          Aplicar Filtros
        </button>

        {hasFilters && (
          <button onClick={handleClear} className="btn btn-secondary text-sm">
            Limpiar filtros
          </button>
        )}
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm text-left border-collapse">
          <thead className="bg-gray-50 border-b">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th key={header.id} className="px-4 py-3 font-medium text-gray-700">
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-4 py-12 text-center text-gray-500">
                  No se encontraron vales
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <tr key={row.id} className="border-b hover:bg-gray-50 transition-colors">
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className="px-4 py-3">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between px-4 py-3 border-t">
        <div className="flex items-center gap-4">
          <span className="text-sm text-gray-500">
            {total} resultado{total !== 1 ? 's' : ''} - Página {page} de {pageCount || 1}
          </span>
          <select
            value={limit}
            onChange={(e) => onLimitChange(Number(e.target.value))}
            className="input w-auto text-sm"
          >
            {[10, 20, 50, 100].map((size) => (
              <option key={size} value={size}>
                {size} por página
              </option>
            ))}
          </select>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => onPageChange(page - 1)}
            disabled={page <= 1}
            className="btn btn-secondary text-sm disabled:opacity-50"
          >
            Anterior
          </button>
          <button
            onClick={() => onPageChange(page + 1)}
            disabled={page >= pageCount}
            className="btn btn-secondary text-sm disabled:opacity-50"
          >
            Siguiente
          </button>
        </div>
      </div>
    </div>
  )
}
